/* eslint-disable @typescript-eslint/no-explicit-any */
import { Field, FieldDescription, FieldError, FieldLabel } from '@eigtech/ui/components/form'
import {
  Tooltip,
  TooltipContent,
  TooltipContentProps,
  TooltipProps,
  TooltipTrigger,
} from '@eigtech/ui/components/tooltip'
import { Info } from 'lucide-react'
import { Control, FieldPath, FieldValues, get, useFormState } from 'react-hook-form'
import { WithFieldNameProp } from '../types/FieldProps'

export type BaseReactHookFormProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = WithFieldNameProp<
  {
    control?: Control<TFieldValues, TName, TFieldValues>
    label?: React.ReactNode
    labelProps?: React.ComponentProps<typeof FieldLabel>
    helperText?: React.ReactNode
    helperTextProps?: React.ComponentProps<typeof FieldDescription>
    errorMessageProps?: React.ComponentProps<typeof FieldError>
    tooltipText?: string
    tooltipProps?: TooltipProps
    tooltipContentProps?: TooltipContentProps
  },
  TFieldValues
>

export type BaseProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  React.ComponentProps<typeof Field>,
  'name'
> &
  BaseReactHookFormProps<TFieldValues>

export function FieldControl<TFieldValues extends FieldValues = FieldValues>(
  props: BaseProps<TFieldValues>
) {
  const {
    children,
    name,
    control,
    label,
    labelProps,
    helperText,
    helperTextProps,
    tooltipText,
    tooltipProps,
    tooltipContentProps,
    errorMessageProps,
    disabled,
    ...rest
  } = props

  const { errors, isSubmitting } = useFormState({ control, name })
  const error = get(errors, name, '') as any

  const hasError = Boolean(error?.message)

  return (
    <Field disabled={isSubmitting || disabled} invalid={hasError} name={name} {...rest}>
      {label && typeof label === 'string' ? (
        <FieldLabel {...labelProps}>
          {label}{' '}
          {tooltipText && (
            <Tooltip {...tooltipProps}>
              <TooltipTrigger>
                <Info
                  aria-label={`Tooltip for form field ${name}`}
                  color="blackAlpha.600"
                  fontSize=".9em"
                />
              </TooltipTrigger>

              <TooltipContent side="right" {...tooltipContentProps}>
                {tooltipText}
              </TooltipContent>
            </Tooltip>
          )}
        </FieldLabel>
      ) : (
        label
      )}

      {children}

      {helperText && typeof helperText === 'string' ? (
        <FieldDescription {...helperTextProps}>{helperText}</FieldDescription>
      ) : (
        helperText
      )}

      {hasError && (
        <FieldError match {...errorMessageProps}>
          {error.message}
        </FieldError>
      )}
    </Field>
  )
}

export type FieldControlComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof FieldControl<TFieldValues>
